import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Mail, MessageCircle, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail, sendEnquiry, subscribeNotify } from "../lib/api";
import { telLink, waLink } from "../lib/site";

export default function EnquiryModal({ open, product, settings, onClose }) {
  const [mode, setMode] = useState("options");
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const comingSoon = product?.availability === "coming_soon";

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
      setMode("options");
      setEmail("");
      setForm({
        name: "",
        phone: "",
        message: product
          ? `Hello Unique Organic Farm, I am interested in ${product.name}. Please share availability and details.`
          : "Hello Unique Organic Farm, I would like to know more about your products. Please share availability and details.",
      });
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [open, product]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendEnquiry({ ...form, product_name: product?.name || "" });
      toast.success("Enquiry sent! The farm will get back to you soon.");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await subscribeNotify({ email, product_id: product?.id || "", product_name: product?.name || "" });
      toast.success(`We'll notify you when ${product?.name || "this product"} is available.`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center sm:p-6"
        >
          <div className="absolute inset-0 bg-nightgreen/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="enquiry-modal"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
            className="relative w-full max-w-md bg-cream rounded-t-3xl sm:rounded-3xl shadow-2xl p-7 sm:p-8 max-h-[90vh] overflow-y-auto"
          >
            <button
              data-testid="enquiry-close-btn"
              onClick={onClose}
              aria-label="Close enquiry"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-beige text-forest flex items-center justify-center hover:bg-sand transition-colors"
            >
              <X className="w-4.5 h-4.5" />
            </button>

            {comingSoon ? (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-gold">Coming Soon</span>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-forest">
                  Notify me about {product.name}
                </h3>
                <p className="mt-3 text-sm font-light text-muted leading-relaxed">
                  This harvest isn't ready yet. Leave your email and we'll reach out the moment it is.
                </p>
                <form onSubmit={submitNotify} className="mt-6 space-y-4">
                  <input
                    data-testid="notify-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
                  />
                  <button
                    data-testid="notify-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-nightgreen transition-all hover:bg-forest hover:text-cream disabled:opacity-60"
                  >
                    <Bell className="w-4 h-4" />
                    {sending ? "Saving…" : "Notify Me"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-gold">Enquire Now</span>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-forest leading-snug">
                  {product ? product.name : "Talk to the Farm"}
                </h3>
                <p className="mt-2 text-sm font-light text-muted">
                  Reach us directly — we reply personally to every enquiry.
                </p>

                {mode === "options" && (
                  <div className="mt-6 space-y-3">
                    <a
                      data-testid="enquiry-call-btn"
                      href={telLink(settings)}
                      className="flex items-center gap-4 rounded-2xl border border-sand bg-white p-4 transition-all duration-300 hover:border-gold hover:shadow-md group"
                    >
                      <span className="w-11 h-11 rounded-full bg-forest text-goldlight flex items-center justify-center shrink-0">
                        <Phone className="w-5 h-5" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-forest">Call Us</span>
                        <span className="block text-xs text-muted mt-0.5">{settings.phone}</span>
                      </span>
                    </a>
                    <a
                      data-testid="enquiry-whatsapp-btn"
                      href={waLink(settings, product?.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 rounded-2xl border border-sand bg-white p-4 transition-all duration-300 hover:border-gold hover:shadow-md group"
                    >
                      <span className="w-11 h-11 rounded-full bg-[#1FA855] text-white flex items-center justify-center shrink-0">
                        <MessageCircle className="w-5 h-5" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-forest">WhatsApp Us</span>
                        <span className="block text-xs text-muted mt-0.5">Message pre-filled{product ? ` for ${product.name}` : ""}</span>
                      </span>
                    </a>
                    <button
                      data-testid="enquiry-form-toggle"
                      onClick={() => setMode("form")}
                      className="w-full flex items-center gap-4 rounded-2xl border border-sand bg-white p-4 text-left transition-all duration-300 hover:border-gold hover:shadow-md"
                    >
                      <span className="w-11 h-11 rounded-full bg-gold text-nightgreen flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-forest">Send Enquiry</span>
                        <span className="block text-xs text-muted mt-0.5">Write to us and we'll call you back</span>
                      </span>
                    </button>
                  </div>
                )}

                {mode === "form" && (
                  <form onSubmit={submitEnquiry} className="mt-6 space-y-3.5" data-testid="enquiry-form">
                    <input
                      data-testid="enquiry-name-input"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
                    />
                    <input
                      data-testid="enquiry-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Your phone number"
                      className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60"
                    />
                    <textarea
                      data-testid="enquiry-message-input"
                      required
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60 resize-none"
                    />
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setMode("options")}
                        className="rounded-full border border-forest/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-forest hover:bg-beige transition-colors"
                      >
                        Back
                      </button>
                      <button
                        data-testid="enquiry-submit-btn"
                        type="submit"
                        disabled={sending}
                        className="flex-1 rounded-full bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-nightgreen transition-all hover:bg-forest hover:text-cream disabled:opacity-60"
                      >
                        {sending ? "Sending…" : "Send Enquiry"}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
